import { ArrowBack, Category as CategoryIcon, ColorLens } from "@mui/icons-material";
import { Box, MenuItem, Select, LinearProgress, IconButton } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateCategory,
  selectAll,
  UpdateRow,
} from "../../../features/CategorySlice";
import * as S from "./styled";
import { createCategory, updateCategory } from "../../../Services/categories";
import taxonomyData from "../../../Assets/taxonomy.json";
// eslint-disable-next-line no-unused-vars
import { auth } from "../../../firebase";
import { CategoryLanguageSelector } from "./components/CategoryLanguageSelector";
import { CategoryLanguageInputs } from "./components/CategoryLanguageInputs";
import { SubcategoriesManager } from "./components/SubcategoriesManager";

export function Register({ editId, closeRegister, onToastOpen, prefilledSuggestion, onApprovedSuggestion }) {
  const CategoryState = useSelector((state) => state.menu.CategoryState);

  const [itemToEdit] = useSelector(selectAll).filter(
    (item) => item.id === editId
  );

  // Lock body scroll when overlay is active
  React.useEffect(() => {
    if (CategoryState) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [CategoryState]);

  return (
    <>
      {CategoryState && (
        <S.OverlayContainer>
          <S.RegisterContainer style={{ minWidth: "460px", maxWidth: "500px" }}>
            <S.RegisterHeader style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
              <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <IconButton size="small" onClick={closeRegister} style={{ backgroundColor: "#181764", color: "#fff" }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
                <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700", color: "#0f172a" }}>
                  {itemToEdit ? "Edição de Categoria" : "Cadastro de Categorias"}
                </h3>
              </Box>
            </S.RegisterHeader>

            <S.RegisterBody style={{ overflowY: "auto", maxHeight: "calc(100vh - 90px)", padding: "12px 24px 30px" }}>
              <CreateForm
                onToastOpen={onToastOpen}
                itemToEdit={itemToEdit}
                closeRegister={closeRegister}
                prefilledSuggestion={prefilledSuggestion}
                onApprovedSuggestion={onApprovedSuggestion}
              />
            </S.RegisterBody>
          </S.RegisterContainer>
        </S.OverlayContainer>
      )}
    </>
  );
}

function CreateForm({ itemToEdit, closeRegister, onToastOpen, prefilledSuggestion, onApprovedSuggestion }) {
  const allCategories = useSelector(selectAll);

  const categoriesTypes = {
    connectroutes: "ConnectRoutes",
    connectstores: "ConnectStores",
    connectplaces: "ConnectPlaces",
    connectguides: "ConnectGuides",
    connectsales: "ConnectSales",
    connectmarket: "ConnectMarket",
  };

  const languagesList = [
    { value: "pt", label: "PT-BR" },
    { value: "en", label: "EN-US" },
    { value: "es", label: "ES" },
    { value: "fr", label: "FR" },
    { value: "it", label: "IT" },
    { value: "zh", label: "ZH" },
    { value: "hi", label: "HI" },
    { value: "ru", label: "RU" },
    { value: "ar", label: "AR" },
    { value: "bn", label: "BN" },
  ];

  // Helper to normalize connection names (singular and plural)
  const normalizeCollection = (col) => {
    if (!col) return "";
    const lower = col.toLowerCase();
    if (lower === "connectsale" || lower === "connectsales") return "connectsales";
    return lower;
  };

  // 2. Idioma / Língua da Interface
  const [currentLanguage, setCurrentLanguage] = useState("pt");

  // 1. Coleção (Tipo)
  const [type, setType] = useState(() => {
    if (itemToEdit && itemToEdit.collection) {
      return normalizeCollection(itemToEdit.collection);
    }
    if (prefilledSuggestion && prefilledSuggestion.collection) {
      return normalizeCollection(prefilledSuggestion.collection);
    }
    return "";
  });

  const [selectedCategoryFromTaxonomy, setSelectedCategoryFromTaxonomy] = useState("");

  const [color, setColor] = useState(() => {
    if (itemToEdit && itemToEdit.color) {
      return itemToEdit.color;
    }
    return "#181764";
  });

  const [names, setNames] = useState(() => {
    if (itemToEdit && itemToEdit.names) {
      const loaded = { ...itemToEdit.names };
      languagesList.forEach((lang) => {
        if (!loaded[lang.value]) {
          loaded[lang.value] = loaded[lang.value + "-BR"] || loaded[lang.value + "-US"] || "";
        }
      });
      return loaded;
    }
    if (prefilledSuggestion && prefilledSuggestion.suggested_name) {
      return {
        pt: prefilledSuggestion.suggested_name,
        "pt-BR": prefilledSuggestion.suggested_name,
        en: "",
        "en-US": "",
        es: "",
        fr: "",
        it: "",
        zh: "",
        hi: "",
        ru: "",
        ar: "",
        bn: "",
      };
    }
    return {
      pt: "",
      "pt-BR": "",
      en: "",
      "en-US": "",
      es: "",
      fr: "",
      it: "",
      zh: "",
      hi: "",
      ru: "",
      ar: "",
      bn: "",
    };
  });

  // Subcategories state
  const [subs, setSubs] = useState(() => {
    return Array.isArray(itemToEdit?.subs) ? itemToEdit.subs : [];
  });

  const [newSubId, setNewSubId] = useState("");
  const [newSubName, setNewSubName] = useState("");
  const [progress, setProgress] = useState(false);

  const [isAiLoading, setIsAiLoading] = useState(false);

  const handleGenerateWithIA = async () => {
    const currentText = names[currentLanguage]?.trim();
    if (!currentText) {
      alert("Por favor, digite o nome da categoria no idioma atual primeiro!");
      return;
    }

    setIsAiLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Usuário não autenticado no Firebase.");
      }
      const token = await currentUser.getIdToken(true);

      const usersApiUrl = process.env.REACT_APP_USERS_API_URL || 
        (window.location.origin.includes("localhost") 
          ? "http://localhost:3000" 
          : window.location.origin.replace("-admin", "-users"));

      const res = await fetch(`${usersApiUrl}/api/ai/taxonomy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          text: currentText,
          lang: currentLanguage
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`API Error: ${res.status} - ${errorText}`);
      }

      const resData = await res.json();
      if (!resData.success || !resData.data) {
        throw new Error(resData.error || "Erro desconhecido na geração com IA");
      }

      const parsed = resData.data;
      
      // Update names mapping pt/en to pt-BR/en-US
      const updatedNames = { ...names, ...parsed.names };
      if (updatedNames.pt) updatedNames["pt-BR"] = updatedNames.pt;
      if (updatedNames.en) updatedNames["en-US"] = updatedNames.en;
      
      languagesList.forEach((lang) => {
        if (!updatedNames[lang.value]) {
          updatedNames[lang.value] = updatedNames[lang.value + "-BR"] || updatedNames[lang.value + "-US"] || "";
        }
      });

      setNames(updatedNames);

      // Process and map subcategories
      if (Array.isArray(parsed.subs)) {
        const mappedSubs = parsed.subs.map((sub) => {
          const subNames = { ...sub.names };
          if (subNames.pt) subNames["pt-BR"] = subNames.pt;
          if (subNames.en) subNames["en-US"] = subNames.en;
          return {
            id: sub.id,
            names: subNames
          };
        });
        setSubs(mappedSubs);
      }

      onToastOpen("Dados da Categoria Gerados com Sucesso!", "success");
    } catch (e) {
      console.error(e);
      alert(`Falha ao gerar com IA: ${e.message}`);
    } finally {
      setIsAiLoading(false);
    }
  };

  const dispatch = useDispatch();

  // Helper to get category name in the currently selected language
  const getLocalizedCatName = (cat, lang) => {
    if (!cat || !cat.names) return cat?.id || "";
    return (
      cat.names[lang] ||
      (lang === "pt" ? cat.names["pt-BR"] : "") ||
      (lang === "en" ? cat.names["en-US"] : "") ||
      cat.names["pt-BR"] ||
      cat.names["pt"] ||
      cat.names["en-US"] ||
      cat.names["en"] ||
      Object.values(cat.names).find((v) => Boolean(v)) ||
      cat.id
    );
  };

  // Get official categories from JSON file matching selected type
  const getJsonCategories = () => {
    const filterType = normalizeCollection(type);
    if (!filterType) {
      return Object.entries(taxonomyData).flatMap(([colName, cats]) =>
        cats.map((c) => ({
          ...c,
          collection: colName,
          isTaxonomyTemplate: true,
        }))
      );
    }
    const cats = taxonomyData[filterType] || [];
    return cats.map((c) => ({
      ...c,
      collection: filterType,
      isTaxonomyTemplate: true,
    }));
  };

  const dbCategories = allCategories.filter((c) => {
    const filterType = normalizeCollection(type);
    const colType = normalizeCollection(c.collection);
    if (!filterType) return true;
    return colType === filterType;
  });

  const jsonCategories = getJsonCategories();

  // Combined list for dropdown selection
  const combinedCategoriesList = [
    ...dbCategories.map((c) => ({ ...c, isFromDb: true })),
    ...jsonCategories.map((c) => ({ ...c, isFromDb: false })),
  ];

  // Auto-fill form when a category is selected from the taxonomy list
  const handleSelectTaxonomyCategory = (compositeKey) => {
    setSelectedCategoryFromTaxonomy(compositeKey);
    if (!compositeKey) {
      setNames({
        pt: "",
        "pt-BR": "",
        en: "",
        "en-US": "",
        es: "",
        fr: "",
        it: "",
        zh: "",
        hi: "",
        ru: "",
        ar: "",
        bn: "",
      });
      setSubs([]);
      setColor("#181764");
      return;
    }

    const parts = compositeKey.split("_");
    const isFromDb = parts.pop() === "db";
    const catId = parts.join("_");

    let cat;
    if (isFromDb) {
      cat = allCategories.find((c) => c.id === catId);
    } else {
      cat = jsonCategories.find((c) => c.id === catId);
    }

    if (cat) {
      if (cat.collection) setType(normalizeCollection(cat.collection));
      if (cat.color) setColor(cat.color);
      if (cat.names) {
        const loaded = { ...cat.names };
        if (loaded.pt) loaded["pt-BR"] = loaded.pt;
        if (loaded.en) loaded["en-US"] = loaded.en;
        languagesList.forEach((lang) => {
          if (!loaded[lang.value]) {
            loaded[lang.value] = loaded[lang.value + "-BR"] || loaded[lang.value + "-US"] || "";
          }
        });
        setNames(loaded);
      }
      if (Array.isArray(cat.subs)) {
        const mappedSubs = cat.subs.map((sub) => {
          const subNames = { ...sub.names };
          if (subNames.pt) subNames["pt-BR"] = subNames.pt;
          if (subNames.en) subNames["en-US"] = subNames.en;
          return {
            id: sub.id,
            names: subNames,
          };
        });
        setSubs(mappedSubs);
      } else {
        setSubs([]);
      }
    }
  };

  const isCorrectForm = (names[currentLanguage] || names["pt"]) && type;

  const handleAddSub = () => {
    const slug = newSubId.trim().toLowerCase().replace(/\s+/g, "_");
    if (!slug) return;

    if (subs.some((sub) => sub.id === slug)) {
      alert("ID de subcategoria já existe!");
      return;
    }

    const newSub = {
      id: slug,
      names: {
        [currentLanguage]: newSubName.trim(),
        ...(currentLanguage === "pt" ? { "pt-BR": newSubName.trim() } : {}),
        ...(currentLanguage === "en" ? { "en-US": newSubName.trim() } : {}),
      },
    };

    setSubs([...subs, newSub]);
    setNewSubId("");
    setNewSubName("");
  };

  const handleRemoveSub = (subId) => {
    setSubs(subs.filter((sub) => sub.id !== subId));
  };

  const handleSubNameChange = (subId, value) => {
    const updated = subs.map((sub) => {
      if (sub.id === subId) {
        return {
          ...sub,
          names: {
            ...sub.names,
            [currentLanguage]: value,
            ...(currentLanguage === "pt" ? { "pt-BR": value } : {}),
            ...(currentLanguage === "en" ? { "en-US": value } : {}),
          },
        };
      }
      return sub;
    });
    setSubs(updated);
  };

  const handleSubmit = async (self) => {
    self.preventDefault();
    setProgress(true);

    const payload = {
      names,
      collection: type,
      color,
      subs,
    };

    const targetItem = itemToEdit || (selectedCategoryFromTaxonomy ? allCategories.find(c => c.id === selectedCategoryFromTaxonomy) : null);

    if (targetItem) {
      try {
        const newCategory = {
          ...targetItem,
          ...payload,
        };
        await updateCategory(newCategory);
        dispatch(UpdateRow(newCategory));
        closeRegister();
        onToastOpen("Success", "success");
      } catch (error) {
        setProgress(false);
        console.error(error);
        onToastOpen("Error", "error");
      }
      return;
    }

    try {
      const id = await createCategory(payload);
      dispatch(CreateCategory({ id, ...payload }));

      if (prefilledSuggestion && prefilledSuggestion.id) {
        const { updateStatus } = await import("../../../Services/suggestions");
        await updateStatus(prefilledSuggestion.id, "approved");
        if (onApprovedSuggestion) onApprovedSuggestion(prefilledSuggestion.id);
      }

      onToastOpen("Success", "success");
      closeRegister();
    } catch (error) {
      setProgress(false);
      console.error(error);
      onToastOpen("Error", "error");
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        
        {/* 1. COLEÇÃO (TIPO) */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.82rem", fontWeight: "700", color: "#1e293b" }}>
            1. Coleção (Tipo) *
          </label>
          <Select
            variant="outlined"
            size="small"
            value={type}
            displayEmpty
            onChange={(e) => {
              setType(e.target.value);
              setSelectedCategoryFromTaxonomy("");
            }}
            style={{ backgroundColor: "#ffffff", borderRadius: "6px", width: "100%" }}
            renderValue={(selected) => {
              if (!selected) {
                return <em style={{ color: "#94a3b8", fontStyle: "normal" }}>Selecione o tipo da coleção</em>;
              }
              return categoriesTypes[selected] || selected;
            }}
          >
            <MenuItem disabled value="">
              <em>Selecione o tipo da coleção</em>
            </MenuItem>
            {Object.entries(categoriesTypes).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* 2. IDIOMA / LÍNGUA DA INTERFACE */}
        <CategoryLanguageSelector
          languagesList={languagesList}
          currentLanguage={currentLanguage}
          setCurrentLanguage={setCurrentLanguage}
          names={names}
        />

        {/* 3. AUTO-CARREGAMENTO DE CATEGORIAS DA TABELA DE TAXONOMIA */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "4px", backgroundColor: "#f8fafc", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}>
          <label style={{ fontSize: "0.78rem", fontWeight: "700", color: "#334155", display: "flex", alignItems: "center", gap: "6px" }}>
            <CategoryIcon style={{ fontSize: "15px", color: "#0284c7" }} />
            3. Carregar Categoria Existente da Taxonomia ({languagesList.find(l => l.value === currentLanguage)?.label})
          </label>
          <Select
            variant="outlined"
            size="small"
            value={selectedCategoryFromTaxonomy}
            disabled={combinedCategoriesList.length === 0}
            onChange={(e) => handleSelectTaxonomyCategory(e.target.value)}
            style={{ backgroundColor: "#ffffff", borderRadius: "6px", fontSize: "0.85rem", width: "100%" }}
          >
            {combinedCategoriesList.length === 0 ? (
              <MenuItem value="">
                <em>Sem categorias para esta Coleção</em>
              </MenuItem>
            ) : (
              [
                <MenuItem key="default" value="">
                  <em>{itemToEdit ? "-- Manter Categoria Atual --" : "-- Criar Nova Categoria --"}</em>
                </MenuItem>
              ].concat(
                combinedCategoriesList.map((cat) => {
                  const localizedName = getLocalizedCatName(cat, currentLanguage);
                  const isDb = cat.isFromDb;
                  const labelPrefix = isDb ? "📂 [Registrada] " : "🏷️ [Taxonomia] ";
                  const optionValue = cat.id + "_" + (isDb ? "db" : "json");
                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {labelPrefix} {localizedName} ({categoriesTypes[normalizeCollection(cat.collection)] || cat.collection}) - {cat.subs?.length || 0} subs
                    </MenuItem>
                  );
                })
              )
            )}
          </Select>
        </Box>

        {/* 4. NOME DA CATEGORIA COM IA */}
        <CategoryLanguageInputs
          currentLanguage={currentLanguage}
          languagesList={languagesList}
          names={names}
          setNames={setNames}
          isAiLoading={isAiLoading}
          handleGenerateWithIA={handleGenerateWithIA}
        />

        {/* 5. SUBCATEGORIAS */}
        <SubcategoriesManager
          subs={subs}
          currentLanguage={currentLanguage}
          languagesList={languagesList}
          handleSubNameChange={handleSubNameChange}
          handleRemoveSub={handleRemoveSub}
          newSubId={newSubId}
          setNewSubId={setNewSubId}
          newSubName={newSubName}
          setNewSubName={setNewSubName}
          handleAddSub={handleAddSub}
        />

        {/* 6. COR DA CATEGORIA */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "8px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff" }}>
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <label style={{ fontSize: "0.82rem", fontWeight: "700", color: "#1e293b", display: "flex", alignItems: "center", gap: "6px" }}>
              <ColorLens style={{ fontSize: "18px", color: color }} />
              6. Cor da Categoria
            </label>
            <Box style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: color, color: "#ffffff", padding: "3px 10px", borderRadius: "12px", fontSize: "0.75rem", fontWeight: "700", shadow: "0 1px 3px rgba(0,0,0,0.15)" }}>
              <span>{names[currentLanguage] || names["pt"] || "Preview Badge"}</span>
            </Box>
          </Box>

          {/* Preset Palette Swatches */}
          <Box style={{ display: "flex", flexWrap: "wrap", gap: "8px", margin: "4px 0" }}>
            {[
              "#181764", "#0284c7", "#059669", "#7c3aed", "#e11d48",
              "#d97706", "#0891b2", "#ea580c", "#4f46e5", "#334155"
            ].map((presetColor) => {
              const isSelected = color.toLowerCase() === presetColor.toLowerCase();
              return (
                <button
                  key={presetColor}
                  type="button"
                  onClick={() => setColor(presetColor)}
                  style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    backgroundColor: presetColor,
                    border: isSelected ? "2px solid #ffffff" : "2px solid transparent",
                    outline: isSelected ? `2px solid ${presetColor}` : "none",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    transform: isSelected ? "scale(1.15)" : "scale(1)",
                  }}
                  title={presetColor}
                />
              );
            })}
          </Box>

          {/* Custom Color Dropper and HEX Text Input */}
          <Box style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "2px" }}>
            <Box style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer"
                }}
              />
              <button
                type="button"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "5px 12px",
                  fontSize: "0.78rem",
                  fontWeight: "600",
                  color: "#334155",
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                <ColorLens style={{ fontSize: "16px", color: color }} />
                Personalizar
              </button>
            </Box>

            <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "0.78rem", fontWeight: "700", color: "#64748b" }}>HEX:</span>
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: "85px",
                  padding: "4px 8px",
                  fontSize: "0.8rem",
                  fontFamily: "monospace",
                  fontWeight: "700",
                  border: "1px solid #cbd5e1",
                  borderRadius: "4px",
                  color: "#0f172a",
                  outline: "none"
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <S.RegisterButton disabled={!isCorrectForm || progress} type="submit" style={{ marginTop: "6px", padding: "8px 20px", fontSize: "0.9rem", fontWeight: "600", borderRadius: "6px" }}>
        {itemToEdit || selectedCategoryFromTaxonomy ? "Salvar Categoria" : "Cadastrar Categoria"}
      </S.RegisterButton>
      {progress && <LinearProgress />}
    </form>
  );
}
